/**
 * DecisionTreeService.ts - Decision tree generation and analysis
 *
 * This service builds decision tree data structures for visualizing
 * all possible paths through a sequence of incidents and highlighting
 * the player's chosen path.
 */

import {
  DecisionNode,
  DecisionPath,
  DecisionTreeData,
  TimelineNode,
  TimelineData,
  TreeBranch,
  TreeStructure
} from '../types/DecisionTree';
import { CategoryPreventionScore } from '../types/PreventionScore';
import { Incident, IncidentResponse, ResponseOption } from '../types/Incident';
import { ScoringWeights } from '../types/ScoringConfiguration';
import {
  calculateCategoryPreventionScores,
  calculateOverallPreventionScore
} from './PreventionScoringService';

/**
 * Build a decision node from an incident and response option
 */
function buildDecisionNode(
  incident: Incident,
  incidentIndex: number,
  option: ResponseOption,
  isSelected: boolean,
  weights?: ScoringWeights
): DecisionNode {
  const preventionScores = calculateCategoryPreventionScores(option.impact);
  const overallScore = calculateOverallPreventionScore(preventionScores, weights);

  return {
    incidentId: incident.id,
    incidentTitle: incident.title,
    incidentIndex,
    responseOptionId: option.id,
    responseLabel: option.label,
    responseDescription: option.description,
    preventionScores,
    overallScore,
    isSelected
  };
}

/**
 * Generate a unique path ID from the response option IDs
 */
function generatePathId(optionIds: string[]): string {
  return optionIds.join('-');
}

/**
 * Recursively generate all possible paths through the incidents
 */
function generateAllPathsRecursive(
  incidents: Incident[],
  currentIndex: number,
  currentPath: DecisionNode[],
  allPaths: DecisionPath[],
  weights?: ScoringWeights
): void {
  if (currentIndex >= incidents.length) {
    // We've completed a path
    const totalScore = currentPath.reduce((sum, node) => sum + node.overallScore, 0);
    const averageScore = currentPath.length > 0 ? Math.round(totalScore / currentPath.length) : 0;
    const pathId = generatePathId(currentPath.map(n => n.responseOptionId));

    allPaths.push({
      nodes: [...currentPath],
      totalScore,
      averageScore,
      pathId
    });
    return;
  }

  const incident = incidents[currentIndex];

  // Try each response option
  for (const option of incident.responseOptions) {
    const node = buildDecisionNode(incident, currentIndex, option, false, weights);
    currentPath.push(node);
    generateAllPathsRecursive(incidents, currentIndex + 1, currentPath, allPaths, weights);
    currentPath.pop();
  }
}

/**
 * Generate all possible paths through the incidents
 */
export function generateAllPaths(
  incidents: Incident[],
  weights?: ScoringWeights
): DecisionPath[] {
  const allPaths: DecisionPath[] = [];
  generateAllPathsRecursive(incidents, 0, [], allPaths, weights);
  return allPaths;
}

/**
 * Build the player's path from their responses
 */
export function buildPlayerPath(
  incidents: Incident[],
  responses: IncidentResponse[],
  weights?: ScoringWeights
): DecisionPath {
  const nodes: DecisionNode[] = [];

  for (let i = 0; i < incidents.length; i++) {
    const incident = incidents[i];
    const response = responses.find(r => r.incidentId === incident.id);

    if (response) {
      // Handle timeout case
      if (response.selectedOptionId === 'timeout') {
        const timeoutNode: DecisionNode = {
          incidentId: incident.id,
          incidentTitle: incident.title,
          incidentIndex: i,
          responseOptionId: 'timeout',
          responseLabel: 'No Response (Timeout)',
          responseDescription: 'Failed to respond within the time limit',
          preventionScores: { security: 0, reputation: 0, financial: 0, operational: 0 },
          overallScore: 0,
          isSelected: true
        };
        nodes.push(timeoutNode);
      } else {
        const option = incident.responseOptions.find(opt => opt.id === response.selectedOptionId);
        if (option) {
          nodes.push(buildDecisionNode(incident, i, option, true, weights));
        }
      }
    }
  }

  const totalScore = nodes.reduce((sum, node) => sum + node.overallScore, 0);
  const averageScore = nodes.length > 0 ? Math.round(totalScore / nodes.length) : 0;
  const pathId = generatePathId(nodes.map(n => n.responseOptionId));

  return {
    nodes,
    totalScore,
    averageScore,
    pathId
  };
}

/**
 * Build complete decision tree data for visualization
 */
export function buildDecisionTree(
  incidents: Incident[],
  responses: IncidentResponse[],
  weights?: ScoringWeights
): DecisionTreeData {
  const playerPath = buildPlayerPath(incidents, responses, weights);
  const allPossiblePaths = generateAllPaths(incidents, weights);

  // Mark the player's path in the all possible paths
  const playerPathId = playerPath.pathId;
  const highlightedPaths = allPossiblePaths.map(path => ({
    ...path,
    nodes: path.nodes.map((node, idx) => ({
      ...node,
      isSelected: path.pathId === playerPathId
    }))
  }));

  // Find best and worst paths
  const sortedPaths = [...highlightedPaths].sort((a, b) => b.averageScore - a.averageScore);
  const bestPath = sortedPaths[0] || playerPath;
  const worstPath = sortedPaths[sortedPaths.length - 1] || playerPath;

  // Calculate player rank
  const playerRank = sortedPaths.findIndex(p => p.pathId === playerPathId) + 1;

  return {
    playerPath,
    allPossiblePaths: highlightedPaths,
    incidents,
    bestPath,
    worstPath,
    playerRank: playerRank > 0 ? playerRank : 1,
    totalPaths: allPossiblePaths.length
  };
}

/**
 * Build timeline data for the simplified horizontal timeline view
 */
export function buildTimelineData(
  incidents: Incident[],
  responses: IncidentResponse[],
  weights?: ScoringWeights
): TimelineData {
  const nodes: TimelineNode[] = [];
  let cumulativeScore = 0;

  for (let i = 0; i < incidents.length; i++) {
    const incident = incidents[i];
    const response = responses.find(r => r.incidentId === incident.id);

    if (response) {
      let preventionScores: CategoryPreventionScore;
      let overallScore: number;
      let label: string;

      if (response.selectedOptionId === 'timeout') {
        preventionScores = { security: 0, reputation: 0, financial: 0, operational: 0 };
        overallScore = 0;
        label = 'No Response (Timeout)';
      } else {
        const option = incident.responseOptions.find(opt => opt.id === response.selectedOptionId);
        if (option) {
          preventionScores = calculateCategoryPreventionScores(option.impact);
          overallScore = calculateOverallPreventionScore(preventionScores, weights);
          label = option.label;
        } else {
          preventionScores = { security: 50, reputation: 50, financial: 50, operational: 50 };
          overallScore = 50;
          label = 'Unknown Response';
        }
      }

      cumulativeScore += overallScore;

      nodes.push({
        incidentId: incident.id,
        incidentTitle: incident.title,
        incidentIndex: i,
        severity: incident.severity,
        selectedResponse: {
          id: response.selectedOptionId,
          label,
          preventionScores,
          overallScore
        },
        timestamp: response.timestamp
      });
    }
  }

  const averageScore = nodes.length > 0 ? Math.round(cumulativeScore / nodes.length) : 0;

  return {
    nodes,
    cumulativeScore,
    averageScore
  };
}

/**
 * Build tree structure for rendering the full branching visualization
 */
export function buildTreeStructure(
  incidents: Incident[],
  responses: IncidentResponse[],
  weights?: ScoringWeights
): TreeStructure {
  // Build player selections map
  const playerSelections = new Map<string, string>();
  for (const response of responses) {
    playerSelections.set(response.incidentId, response.selectedOptionId);
  }

  // Generate all paths to count how many go through each option
  const allPaths = generateAllPaths(incidents, weights);

  // Build branches
  const branches: TreeBranch[] = incidents.map((incident, incidentIndex) => {
    const options = incident.responseOptions.map(option => {
      const preventionScores = calculateCategoryPreventionScores(option.impact);
      const overallScore = calculateOverallPreventionScore(preventionScores, weights);
      const isSelected = playerSelections.get(incident.id) === option.id;

      // Count paths that go through this option
      const leadsToPaths = allPaths.filter(path =>
        path.nodes[incidentIndex]?.responseOptionId === option.id
      ).length;

      return {
        option,
        preventionScores,
        overallScore,
        isSelected,
        leadsToPaths
      };
    });

    return {
      incident,
      incidentIndex,
      options
    };
  });

  return {
    branches,
    playerSelections
  };
}

/**
 * Get comparison stats between player path and optimal path
 */
export function getPathComparison(treeData: DecisionTreeData): {
  playerScore: number;
  bestScore: number;
  worstScore: number;
  percentile: number;
  improvementPotential: number;
} {
  const playerScore = treeData.playerPath.averageScore;
  const bestScore = treeData.bestPath.averageScore;
  const worstScore = treeData.worstPath.averageScore;

  // Calculate percentile (100 = best, 0 = worst)
  const range = bestScore - worstScore;
  const percentile = range > 0
    ? Math.round(((playerScore - worstScore) / range) * 100)
    : 50;

  // Calculate improvement potential
  const improvementPotential = Math.max(0, bestScore - playerScore);

  return {
    playerScore,
    bestScore,
    worstScore,
    percentile,
    improvementPotential
  };
}
